import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://brontointelligence.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = 'Bronto Intelligence';

/**
 * Per-page SEO component.
 * - Sets a unique <title>, meta description, and rel=canonical for every route.
 * - Mirrors the OG and Twitter blocks with route-specific values.
 *
 * Props:
 *   title         (string, required)  - Page <title>. Will be suffixed with site name automatically unless already present.
 *   description   (string, required)  - Meta description for the page.
 *   path          (string, required)  - Absolute path on this domain, must start with "/". e.g. "/about"
 *   image         (string, optional)  - Full URL to a custom OG image. Defaults to /og-image.png.
 *   noindex       (bool,   optional)  - If true, adds <meta name="robots" content="noindex,follow"> for 404 etc.
 */
export const SEO = ({ title, description, path, image, noindex = false }) => {
  const safePath = path && path.startsWith('/') ? path : '/';
  const canonical = `${SITE_URL}${safePath === '/' ? '/' : safePath.replace(/\/+$/, '')}`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,follow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default SEO;
